import { useEffect, useLayoutEffect, useMemo, useState, createContext, useContext } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, RefreshCcw, Repeat, ShieldCheck, X, Copy, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, extractError } from "../lib/api";
import { Button } from "./ui/button";
import { Alert, AlertDescription } from "./ui/alert";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Textarea } from "./ui/textarea";

// ---------------------------------------------------------------------------
// Repeat-history context — the drawer is mounted once at the page level; per-
// correction chips call `openHistory(correction, meta)` to show it.
// ---------------------------------------------------------------------------
const RepeatHistoryContext = createContext(null);

export function useRepeatHistory() {
  const ctx = useContext(RepeatHistoryContext);
  if (!ctx) throw new Error("useRepeatHistory must be used inside <RepeatHistoryProvider>");
  return ctx;
}

// Shared cache across all chips/drawer for a page render.
export function RepeatHistoryProvider({ children, onNavigateToInvoice }) {
  const [target, setTarget] = useState(null);          // { correctionId, meta }
  const [cache, setCache] = useState({});              // { [correctionId]: { 12: report, 24: report } }
  const [previewId, setPreviewId] = useState(null);    // invoice_id currently shown in the LEFT-side preview modal

  const openHistory = (correctionId, meta) => setTarget({ correctionId, meta });
  const closeHistory = () => setTarget(null);
  const putReport = (correctionId, months, report) => {
    setCache((c) => ({ ...c, [correctionId]: { ...(c[correctionId] || {}), [months]: report } }));
  };
  const getReport = (correctionId, months) => cache?.[correctionId]?.[months];
  const openPreview = (invoiceId) => setPreviewId(invoiceId || null);
  const closePreview = () => setPreviewId(null);

  const value = { openHistory, closeHistory, putReport, getReport, openPreview, closePreview };
  return (
    <RepeatHistoryContext.Provider value={value}>
      {children}
      <RepeatHistoryDrawer target={target} onClose={closeHistory} onNavigateToInvoice={onNavigateToInvoice} previewOpen={!!previewId} />
      <InvoicePreviewPanel invoiceId={previewId} onClose={closePreview} />
    </RepeatHistoryContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Row-level pill — table Repeats column
// ---------------------------------------------------------------------------
export function RepeatRowPill({ invoice }) {
  const n = invoice?.repeat_count || 0;
  if (!n) return <span className="text-slate-300">—</span>;
  if (invoice.possible_warranty) {
    return (
      <span
        className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[11px] font-semibold text-rose-700"
        title={`${n} prior repair(s) on this unit within 12 months — same shop`}
        data-testid={`repair-repeat-pill-${invoice.invoice_number}`}
      >
        <AlertTriangle className="h-3 w-3" /> {n} prior · warranty?
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
      title={`${n} prior repair(s) on this unit within 12 months`}
      data-testid={`repair-repeat-pill-${invoice.invoice_number}`}
    >
      <Repeat className="h-3 w-3" /> {n} prior
    </span>
  );
}

// ---------------------------------------------------------------------------
// Per-correction chip — clicking opens the drawer
// ---------------------------------------------------------------------------
const TIER_META = {
  exact:       { label: "Exact match", cls: "border-rose-200 bg-rose-50 text-rose-700" },
  similar:     { label: "Similar",     cls: "border-amber-200 bg-amber-50 text-amber-700" },
  same_system: { label: "Same system", cls: "border-slate-300 bg-slate-100 text-slate-700" },
};

// Compact "mileage since" chip shown next to the "N mo ago" label in each
// prior-repair row. Renders nothing when either side lacks mileage (matches
// the drawer's existing "unknown → hide" pattern). When both sides come
// from Samsara the delta is telemetry-based and prefixed with `≈` plus a
// small "Samsara" tag; when either side is FullBay `chassis` (shop-verified
// odometer), no tag is shown and the number stands as authoritative.
function MileageDelta({ current, currentSource, prior, priorSource, priorInvoiceNumber }) {
  if (current == null || prior == null) return null;
  const delta = Math.round(Math.abs(Number(current) - Number(prior)));
  if (!isFinite(delta)) return null;
  const bothSamsara = currentSource === "samsara" && priorSource === "samsara";
  const prefix = bothSamsara ? "≈" : "";
  return (
    <span className="inline-flex items-center gap-1" data-testid={`mileage-delta-${priorInvoiceNumber}`}>
      <span className="text-[11px] text-slate-500">
        {prefix}{delta.toLocaleString()} mi since
      </span>
      {bothSamsara && (
        <span
          className="rounded-full border border-slate-200 bg-slate-50 px-1 py-0 text-[9px] font-medium uppercase tracking-wide text-slate-500"
          title="Both mileage readings come from Samsara telemetry (less precise than a shop-verified chassis odometer)"
        >
          Samsara
        </span>
      )}
    </span>
  );
}


// Precedence: exact > similar > same_system
function strongestTier(report) {
  if (!report || !report.items?.length) return null;
  if (report.counts?.exact) return "exact";
  if (report.counts?.similar) return "similar";
  if (report.counts?.same_system) return "same_system";
  return null;
}

export function RepeatCorrectionChip({ correctionId, correction, invoiceMeta, parentHasRepeats }) {
  const { openHistory, putReport, getReport } = useRepeatHistory();
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(() => getReport(correctionId, 12) || null);

  // Skip the fetch entirely when the parent invoice's rollup says no repeats.
  useEffect(() => {
    if (!parentHasRepeats) return;
    if (report) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/equipment-repairs/corrections/${correctionId}/history`, { params: { window_months: 12 } })
      .then((r) => {
        if (cancelled) return;
        setReport(r.data);
        putReport(correctionId, 12, r.data);
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctionId, parentHasRepeats]);

  if (!parentHasRepeats) return null;
  if (loading) {
    return <span className="text-[11px] text-slate-400" data-testid={`repair-history-chip-loading-${correctionId}`}>…</span>;
  }
  const tier = strongestTier(report);
  // Phase 4a fix — the tier chip and the "Reviewed" resolution pill are
  // independent surfaces. A correction with `repeat_resolution` set
  // should always show the pill, even if the tier chip is hidden
  // (defensive: the report may lag). Render both when applicable.
  const resolution = correction?.repeat_resolution || null;
  if (!tier && !resolution) return null;
  const meta = tier ? TIER_META[tier] : null;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {tier && (
        <button
          type="button"
          onClick={() => openHistory(correctionId, { correction, invoice: invoiceMeta })}
          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold hover:brightness-95 ${meta.cls}`}
          data-testid={`repair-history-chip-${correctionId}`}
          title="Open prior-repair history"
        >
          <Repeat className="h-3 w-3" /> {meta.label}
          {report.counts.total > 1 && <span className="ml-0.5">· {report.counts.total}</span>}
        </button>
      )}
      {resolution && (
        <ReviewedPill
          resolution={resolution}
          by={correction?.repeat_resolved_by}
          at={correction?.repeat_resolved_at}
          note={correction?.repeat_dismiss_note}
        />
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Right-side drawer
// ---------------------------------------------------------------------------
function RepeatHistoryDrawer({ target, onClose, onNavigateToInvoice, previewOpen = false }) {
  const [windowMonths, setWindowMonths] = useState(12);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [sameShopOnly, setSameShopOnly] = useState(false);
  const { putReport, getReport, openPreview: openPreviewInvoice } = useRepeatHistory();

  // Reset UI when opening a new correction.
  useEffect(() => {
    if (!target) return;
    setWindowMonths(12); setTierFilter("all"); setSameShopOnly(false); setErr("");
  }, [target?.correctionId]);

  useEffect(() => {
    if (!target) return;
    const cached = getReport(target.correctionId, windowMonths);
    if (cached) { setReport(cached); return; }
    let cancelled = false;
    setLoading(true); setErr("");
    api.get(`/equipment-repairs/corrections/${target.correctionId}/history`, {
      params: { window_months: windowMonths },
    })
      .then((r) => {
        if (cancelled) return;
        setReport(r.data);
        putReport(target.correctionId, windowMonths, r.data);
      })
      .catch((e) => { if (!cancelled) setErr(extractError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.correctionId, windowMonths]);

  const visible = useMemo(() => {
    const items = report?.items || [];
    return items.filter((it) => {
      if (tierFilter !== "all" && it.tier !== tierFilter) return false;
      if (sameShopOnly && !it.same_shop) return false;
      return true;
    });
  }, [report, tierFilter, sameShopOnly]);

  const open = !!target;
  const meta = target?.meta || {};
  const corr = meta.correction || {};
  const inv = meta.invoice || {};

  // Phase 4a — repeat-repair resolution helpers. All three actions target
  // the same correction and, on success, close the drawer + reload the
  // parent invoice. Duplicate + Warranty commit an allocation
  // (charge_type=duplicate|warranty, no expense, no Alvys), and Dismiss
  // sets a resolution flag without touching allocations.
  const [dismissOpen, setDismissOpen] = useState(false);
  const [dismissNote, setDismissNote] = useState("");
  const [dismissing, setDismissing] = useState(false);
  const [busy, setBusy] = useState(false);
  const priorInvoiceNumber = report?.most_recent?.invoice_number || null;
  const correctionStatus = meta?.correction?.status;
  const alreadyAllocated = correctionStatus === "charged"
                        || correctionStatus === "assigned"
                        || correctionStatus === "partial";
  const existingResolution = meta?.correction?.repeat_resolution || null;

  const closeAndRefreshParent = () => {
    const cid = target?.correctionId;
    window.dispatchEvent(new CustomEvent("repairs:invoice-reload", {
      detail: { invoiceId: meta?.invoice?.id },
    }));
    window.dispatchEvent(new Event("repairs:refresh-badge"));
    window.dispatchEvent(new Event("shop-credits:refresh-badge"));
    onClose();
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="correction-row-${cid}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.classList.add("ring-2", "ring-rose-400");
        setTimeout(() => el.classList.remove("ring-2", "ring-rose-400"), 2500);
      }
    }, 400);
  };

  const flagForWarranty = async () => {
    if (!target || !report?.possible_warranty) return;
    const amount = Number(meta?.correction?.amount || 0);
    if (!amount) {
      toast.error("Cannot flag: correction amount unknown");
      return;
    }
    setBusy(true);
    try {
      await api.put(`/equipment-repairs/corrections/${target.correctionId}/allocations`, {
        allocations: [{
          charge_type: "warranty",
          amount,
          note: priorInvoiceNumber
            ? `Warranty candidate — see prior invoice #${priorInvoiceNumber}`
            : "Warranty candidate — see prior repair on this unit",
        }],
        repeat_resolution: "warranty",
        repeat_prior_invoice_number: priorInvoiceNumber || undefined,
      });
      toast.success(priorInvoiceNumber
        ? `Marked as warranty — draft claim created. Prior invoice: #${priorInvoiceNumber}`
        : "Marked as warranty — draft claim created.");
      closeAndRefreshParent();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const markAsDuplicate = async () => {
    if (!target) return;
    const amount = Number(meta?.correction?.amount || 0);
    if (!amount) {
      toast.error("Cannot mark duplicate: correction amount unknown");
      return;
    }
    setBusy(true);
    try {
      // Phase 4a — duplicate is a pass-through allocation (no expense, no
      // Alvys). Phase 4b Shop Credits page will consume `charge_type=
      // duplicate` corrections and open a shop-credit claim from there.
      await api.put(`/equipment-repairs/corrections/${target.correctionId}/allocations`, {
        allocations: [{
          charge_type: "duplicate",
          amount,
          note: priorInvoiceNumber
            ? `Duplicate of invoice #${priorInvoiceNumber}`
            : "Duplicate of a prior repair on this unit",
        }],
        repeat_resolution: "duplicate",
        repeat_prior_invoice_number: priorInvoiceNumber || undefined,
      });
      toast.success(priorInvoiceNumber
        ? `Marked as duplicate — draft claim created. Prior invoice: #${priorInvoiceNumber}`
        : "Marked as duplicate — draft claim created.");
      closeAndRefreshParent();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const dismissRepeat = async () => {
    if (!target) return;
    setDismissing(true);
    try {
      await api.post(`/equipment-repairs/corrections/${target.correctionId}/dismiss-repeat`, {
        note: dismissNote.trim() || undefined,
      });
      toast.success("Repeat dismissed");
      setDismissOpen(false);
      setDismissNote("");
      closeAndRefreshParent();
    } catch (e) {
      toast.error(extractError(e));
    } finally {
      setDismissing(false);
    }
  };

  const tierPills = [
    { v: "all",         l: "All" },
    { v: "exact",       l: "Exact" },
    { v: "similar",     l: "Similar" },
    { v: "same_system", l: "Same system" },
  ];

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-lg overflow-y-auto p-0"
        data-testid="repair-history-drawer"
        onEscapeKeyDown={(e) => {
          // When the preview modal is open, Escape closes only the preview
          // (handled inside InvoicePreviewPanel) — keep the drawer open.
          if (previewOpen) e.preventDefault();
        }}
        onPointerDownOutside={(e) => {
          // Never let outside pointerdowns close the drawer while the preview
          // modal is up.  The modal owns close semantics for that interaction.
          if (previewOpen) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (previewOpen) e.preventDefault();
        }}
        onFocusOutside={(e) => {
          if (previewOpen) e.preventDefault();
        }}
      >
        <SheetHeader className="border-b border-slate-200 px-6 pt-6 pb-4">
          <SheetTitle className="text-base">Prior repairs on this unit</SheetTitle>
          <SheetDescription className="text-xs">
            <span className="font-mono">{inv.unit_number || "—"}</span>
            {inv.vin && <> · VIN <span className="font-mono">{inv.vin}</span></>}
            {inv.invoice_number && <> · from invoice <span className="font-mono">#{inv.invoice_number}</span></>}
          </SheetDescription>
          <div className="mt-2 rounded-md bg-slate-50 p-2 text-xs text-slate-700">
            <div className="font-semibold uppercase tracking-wide text-slate-500 text-[10px]">This correction</div>
            <div className="mt-0.5 line-clamp-3 whitespace-pre-line">{corr.description || "—"}</div>
            {(corr.global_component || corr.global_system) && (
              <div className="mt-1 text-[11px] text-slate-500">
                {corr.global_component}{corr.global_component && corr.global_system ? " · " : ""}{corr.global_system}
              </div>
            )}
          </div>
        </SheetHeader>

        <div className="border-b border-slate-200 px-6 py-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Tier</span>
            {tierPills.map((p) => (
              <button
                key={p.v}
                type="button"
                onClick={() => setTierFilter(p.v)}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                  tierFilter === p.v ? "bg-[#0F1E3D] text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                }`}
                data-testid={`repair-history-tier-${p.v}`}
              >
                {p.l}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setSameShopOnly((v) => !v)}
              className={`ml-auto rounded-full px-2.5 py-0.5 text-[11px] font-medium ${
                sameShopOnly ? "bg-rose-600 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
              data-testid="repair-history-sameshop-toggle"
            >
              Same shop only
            </button>
          </div>
          {report && (
            <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
              <div>
                Window: last {report.window_months} mo · counts:{" "}
                <span className="text-rose-700">{report.counts.exact} exact</span> ·{" "}
                <span className="text-amber-700">{report.counts.similar} similar</span> ·{" "}
                <span className="text-slate-600">{report.counts.same_system} same-system</span>
              </div>
              {report.possible_warranty && (
                <span className="rounded-full bg-rose-50 px-2 py-0.5 font-semibold text-rose-700">
                  possible warranty
                </span>
              )}
              {existingResolution && (
                <ReviewedPill
                  resolution={existingResolution}
                  by={meta?.correction?.repeat_resolved_by}
                  at={meta?.correction?.repeat_resolved_at}
                  note={meta?.correction?.repeat_dismiss_note}
                />
              )}
            </div>
          )}

          {/* Phase 4a — three-action row. Dismiss + Duplicate are always
              visible on a repeat correction; Flag-for-warranty gates on
              `possible_warranty` (only meaningful when a prior repair is
              inside the warranty window). All three actions are disabled
              when the correction has already been allocated (any status
              other than `unassigned`) — reversing the existing allocation
              is the way back. */}
          {report && (report.counts.exact + report.counts.similar + report.counts.same_system > 0) && (
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setDismissOpen(true)}
                disabled={busy || alreadyAllocated}
                className="h-7 border-slate-300 bg-white text-slate-700 hover:bg-slate-100"
                data-testid={`repeat-dismiss-${target?.correctionId}`}
                title={alreadyAllocated
                  ? "Correction already allocated — reverse the charge first"
                  : "Mark this repeat as reviewed. Doesn't allocate anything."}
              >
                <X className="mr-1 h-3 w-3" /> Dismiss Repeat
              </Button>
              <Button
                type="button"
                size="sm"
                onClick={markAsDuplicate}
                disabled={busy || alreadyAllocated}
                className="h-7 bg-orange-500 text-white hover:bg-orange-600"
                data-testid={`repeat-duplicate-${target?.correctionId}`}
                title={alreadyAllocated
                  ? "Correction already allocated — reverse the charge first"
                  : "Mark as duplicate. Queues for a shop credit claim (Phase 4b)."}
              >
                <Copy className="mr-1 h-3 w-3" /> Duplicate
              </Button>
              {report.possible_warranty && (
                <Button
                  type="button"
                  size="sm"
                  onClick={flagForWarranty}
                  disabled={busy || alreadyAllocated}
                  className="h-7 bg-rose-600 text-white hover:bg-rose-700"
                  data-testid={`warranty-flag-${target?.correctionId}`}
                  title={alreadyAllocated
                    ? "Correction already allocated — reverse the charge first"
                    : "Pre-fill this correction as warranty and drop a link to the prior invoice"}
                >
                  <ShieldCheck className="mr-1 h-3 w-3" /> Flag for warranty
                </Button>
              )}
              {alreadyAllocated && (
                <span className="text-[10px] italic text-slate-400">
                  already {correctionStatus} — reverse first to re-decide
                </span>
              )}
            </div>
          )}
        </div>

        {/* Dismiss confirmation modal — optional note. */}
        <Dialog open={dismissOpen} onOpenChange={(v) => { if (!dismissing) setDismissOpen(v); }}>
          <DialogContent data-testid={`repeat-dismiss-dialog-${target?.correctionId}`}>
            <DialogHeader>
              <DialogTitle>Dismiss this repeat?</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 text-sm text-slate-700">
              <p>
                Marks the repeat-match as reviewed. The correction is not
                allocated — you still need to allocate it via the normal
                chips (Warranty / Company / Driver / Lessee / Split).
              </p>
              <div>
                <label htmlFor="dismiss-note" className="mb-1 block text-xs font-medium text-slate-600">
                  Reason (optional)
                </label>
                <Textarea
                  id="dismiss-note"
                  value={dismissNote}
                  onChange={(e) => setDismissNote(e.target.value)}
                  placeholder="e.g. Different failure mode, unrelated repair"
                  maxLength={500}
                  className="min-h-[70px]"
                  data-testid={`repeat-dismiss-note-${target?.correctionId}`}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDismissOpen(false)}
                disabled={dismissing}
                data-testid={`repeat-dismiss-cancel-${target?.correctionId}`}
              >
                Cancel
              </Button>
              <Button
                onClick={dismissRepeat}
                disabled={dismissing}
                data-testid={`repeat-dismiss-confirm-${target?.correctionId}`}
              >
                {dismissing ? "Dismissing…" : "Dismiss"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <div className="px-6 py-4">
          {loading && <div className="text-sm text-slate-500">Loading…</div>}
          {err && <Alert variant="destructive"><AlertDescription>{err}</AlertDescription></Alert>}
          {!loading && !err && report && visible.length === 0 && (
            <div className="rounded-md border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500">
              No prior repairs on this unit within {report.window_months} months.
              {report.window_months < 24 && (
                <div className="mt-2">
                  <Button variant="outline" size="sm" onClick={() => setWindowMonths(24)} data-testid="repair-history-expand-window">
                    <RefreshCcw className="mr-2 h-3 w-3" /> Expand to 24 months
                  </Button>
                </div>
              )}
            </div>
          )}
          {!loading && !err && visible.length > 0 && (
            <ol className="space-y-3">
              {visible.map((it) => {
                const tier = TIER_META[it.tier] || TIER_META.same_system;
                return (
                  <li
                    key={it.correction_id}
                    className={`rounded-md border p-3 ${it.same_shop ? "border-rose-200 bg-rose-50/40" : "border-slate-200 bg-white"}`}
                    data-testid={`repair-history-item-${it.correction_id}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide ${tier.cls}`}>
                        {tier.label}
                      </span>
                      <div className="flex items-center gap-2 text-[11px] text-slate-500" title={it.invoice_date}>
                        <MileageDelta
                          current={report.current_mileage}
                          currentSource={report.current_mileage_source}
                          prior={it.mileage}
                          priorSource={it.mileage_source}
                          priorInvoiceNumber={it.invoice_number}
                        />
                        <span>{it.months_ago === 0 ? "less than 1 mo ago" : `${it.months_ago} mo ago`}</span>
                      </div>
                    </div>
                    <div className="mt-1.5 flex items-baseline gap-2">
                      <button
                        type="button"
                        onClick={() => openPreviewInvoice(it.invoice_id)}
                        className="font-mono text-sm font-semibold text-[#0F1E3D] hover:underline"
                        data-testid={`repair-history-open-preview-${it.invoice_number || it.invoice_id}`}
                      >
                        #{it.invoice_number || "—"}
                      </button>
                      <span className="text-xs text-slate-400">{it.invoice_date}</span>
                      <span className="ml-auto text-sm font-semibold text-slate-900">${(it.amount || 0).toFixed(2)}</span>
                    </div>
                    <div className={`mt-1 text-xs ${it.same_shop ? "font-semibold text-rose-700" : "text-slate-500"}`}>
                      {it.shop_name || "unknown shop"}
                      {it.same_shop && <span className="ml-1 rounded-full bg-rose-100 px-1.5 py-0 text-[10px] font-semibold uppercase">same shop</span>}
                    </div>
                    <div className="mt-1.5 whitespace-pre-line text-sm text-slate-800">
                      {it.description}
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}


// ---------------------------------------------------------------------------
// Reviewed pill — rendered when `correction.repeat_resolution` is set.
// Three colored variants (warranty/duplicate/dismissed) so the operator
// can tell at-a-glance how a repeat was previously handled.
// ---------------------------------------------------------------------------
const RESOLUTION_META = {
  warranty:  { label: "Reviewed · Warranty",  classes: "bg-violet-100 text-violet-800" },
  duplicate: { label: "Reviewed · Duplicate", classes: "bg-orange-100 text-orange-800" },
  dismissed: { label: "Reviewed · Dismissed", classes: "bg-slate-200 text-slate-700" },
};

function ReviewedPill({ resolution, by, at, note }) {
  const meta = RESOLUTION_META[resolution];
  if (!meta) return null;
  const when = at ? new Date(at) : null;
  const tstamp = when && !Number.isNaN(when.getTime())
    ? when.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
    : null;
  const tip = [
    by ? `By ${by}` : null,
    tstamp ? `At ${tstamp}` : null,
    note ? `Note: ${note}` : null,
  ].filter(Boolean).join(" · ");
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${meta.classes}`}
      title={tip || undefined}
      data-testid={`repeat-reviewed-pill-${resolution}`}
    >
      <CheckCircle2 className="h-3 w-3" />
      {meta.label}
    </span>
  );
}


// ---------------------------------------------------------------------------
// Left-side preview modal for a prior invoice — opened when the drawer's
// "Prior repairs on this unit" list has an invoice number clicked.  The
// drawer stays visible on the right (its outside-interaction handlers
// pre-empt close while the preview is open).  Content is strictly read-only.
// Enters/exits with a 300ms slide-from-right animation mirroring the
// drawer's own Radix Sheet timing.
// ---------------------------------------------------------------------------
function InvoicePreviewPanel({ invoiceId, onClose }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  // Local render state — kept mounted while the exit animation plays.
  const [renderedId, setRenderedId] = useState(invoiceId || null);
  const [entered, setEntered] = useState(false);

  // Sync prop -> local state.  New id: swap content + slide in.  Null id:
  // slide out first, then unmount on transitionend.
  useEffect(() => {
    if (invoiceId) {
      setRenderedId(invoiceId);
      // Two rAFs: mount frame → paint → transform kicks in.
      const id1 = requestAnimationFrame(() => {
        const id2 = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(id2);
      });
      return () => cancelAnimationFrame(id1);
    }
    // Parent unset invoiceId — start exit animation.
    setEntered(false);
    return undefined;
  }, [invoiceId]);

  // Fetch detail whenever the rendered id changes.
  useEffect(() => {
    if (!renderedId) { setDetail(null); setErr(""); return; }
    let cancelled = false;
    setLoading(true); setErr(""); setDetail(null);
    api.get(`/equipment-repairs/invoices/${renderedId}`)
      .then((r) => { if (!cancelled) setDetail(r.data); })
      .catch((e) => { if (!cancelled) setErr(extractError(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [renderedId]);

  // Escape closes only the preview — capture phase so Radix's document-level
  // Escape listener doesn't also close the drawer.
  useEffect(() => {
    if (!renderedId) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.stopPropagation(); onClose(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [renderedId, onClose]);

  // Suppress the drawer's Radix Sheet overlay dim while the preview is
  // mounted so the modal renders at full contrast.  Original inline
  // background is captured and restored on cleanup.  useLayoutEffect so
  // both the neutralisation (on mount) and restoration (on unmount) run
  // synchronously with commit — before the browser paints the frame — so
  // the custom dim rectangle and the Radix overlay never simultaneously
  // present an un-dimmed page (which would flicker at open/close).
  useLayoutEffect(() => {
    if (!renderedId) return;
    const overlay = document.querySelector('div[data-state="open"][aria-hidden="true"]');
    if (!overlay) return undefined;
    const prevBg = overlay.style.background;
    const prevPe = overlay.style.pointerEvents;
    overlay.style.background = "transparent";
    // Overlay is at z-50 (same as drawer content) and covers inset-0;
    // without disabling its pointer events, clicks on the modal at z-40
    // would be captured by the overlay.  The drawer's onPointerDownOutside
    // / onInteractOutside handlers already preventDefault while the preview
    // is up, so bypassing the overlay does not cause a drawer close.
    overlay.style.pointerEvents = "none";
    return () => {
      overlay.style.background = prevBg;
      overlay.style.pointerEvents = prevPe;
    };
  }, [renderedId]);

  const handleTransitionEnd = (e) => {
    // Only react to the panel's own transform transition, not descendants.
    if (e.propertyName !== "transform") return;
    if (!invoiceId && !entered) setRenderedId(null);
  };

  if (!renderedId) return null;

  const money = (v) => v == null ? "—" : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const short = (d) => d ? String(d).slice(0, 10) : "—";

  // Bucket allocations by charge_type for a compact static badge line.
  const bucketBadges = (corr) => {
    const badges = [];
    for (const a of corr?.charge_allocations || []) {
      badges.push({
        key: `${corr.id}-${a.charge_type}-${a.amount}`,
        label: `${a.charge_type}: ${money(a.amount)}`,
        cls: {
          company:   "bg-indigo-50 text-indigo-700 border-indigo-200",
          driver:    "bg-emerald-50 text-emerald-700 border-emerald-200",
          warranty:  "bg-violet-50 text-violet-700 border-violet-200",
          duplicate: "bg-orange-50 text-orange-700 border-orange-200",
        }[a.charge_type] || "bg-slate-50 text-slate-700 border-slate-200",
      });
    }
    return badges;
  };

  const CORRECTION_STATUS_CLS = {
    unassigned: "bg-slate-100 text-slate-700",
    assigned:   "bg-blue-100 text-blue-800",
    charged:    "bg-emerald-100 text-emerald-800",
    voided:     "bg-slate-200 text-slate-500",
  };

  return createPortal(
    <>
      {/* Custom dim rectangle beneath the preview — sits below the panel
          (z-30 < panel z-40 < drawer z-50) so both the modal and the drawer
          render fully bright while the rest of the viewport is dimmed.
          The Radix Sheet overlay itself is neutralised (see effect above)
          because it covered the modal at z-50.  Instant on/off (no opacity
          fade) so it stays perfectly in sync with the overlay neutralisation
          — any fade here would flicker the page dim on open/close. */}
      <div
        aria-hidden="true"
        data-testid="invoice-preview-dim"
        className="fixed inset-0 z-30 bg-black/80 pointer-events-none"
      />
      {/* Panel — no backdrop.  Closes only via the Close button or Escape
          key; click-outside is intentionally NOT a close path so accounting
          can inspect the modal while interacting with the drawer/list. */}
      <div
        data-invoice-preview="panel"
        role="dialog"
        aria-modal="false"
        aria-label={`Preview invoice ${detail?.invoice_number || ""}`}
        onTransitionEnd={handleTransitionEnd}
        className={
          "fixed inset-y-4 sm:right-[calc(32rem+2px)] z-40 pointer-events-auto flex w-[min(50vw,900px)] min-w-[640px] max-w-[900px] flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out "
          + (entered ? "translate-x-0" : "translate-x-full")
        }
        data-testid="invoice-preview-panel"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
          <div className="min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="font-mono text-base font-semibold text-slate-900">
                #{detail?.invoice_number || (loading ? "…" : "—")}
              </span>
              <span className="text-xs text-slate-500">{short(detail?.invoice_date)}</span>
              {detail?.rollup_status && (
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                  {detail.rollup_status}
                </span>
              )}
              {detail?.fullbay_payment_status && (
                <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                  {detail.fullbay_payment_status}
                </span>
              )}
              {detail?.fullbay_deleted_at && (
                <span
                  className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800"
                  data-testid="invoice-preview-deleted-badge"
                  title={`Deleted from FullBay on ${short(detail.fullbay_deleted_at)}`}
                >
                  Deleted from FullBay · {short(detail.fullbay_deleted_at)}
                </span>
              )}
            </div>
            <div className="mt-1 text-xs text-slate-500">
              {detail?.raw_payload?.shopTitle || detail?.shop_name || "Unknown shop"}
              {detail?.service_date && <> · Service {short(detail.service_date)}</>}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            data-testid="invoice-preview-close"
          >
            Close
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading && (
            <div className="flex items-center justify-center py-8 text-sm text-slate-500" data-testid="invoice-preview-loading">
              <RefreshCcw className="mr-2 h-4 w-4 animate-spin" /> Loading invoice…
            </div>
          )}
          {!!err && !loading && (
            <Alert className="border-rose-200 bg-rose-50 text-rose-800" data-testid="invoice-preview-error">
              <AlertDescription>{err}</AlertDescription>
              <div className="mt-2">
                <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
              </div>
            </Alert>
          )}
          {!loading && !err && detail && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs sm:grid-cols-4">
                <MetaKV label="Unit"  value={detail.unit_number || "—"} mono />
                <MetaKV label="VIN"   value={detail.vin || "—"} mono />
                <MetaKV label="Chassis mi" value={detail.chassis_mileage != null ? Number(detail.chassis_mileage).toLocaleString() : "—"} />
                <MetaKV label="Samsara mi" value={detail.samsara_mileage_at_service != null ? Number(detail.samsara_mileage_at_service).toLocaleString() : "—"} />
                <MetaKV label="Total" value={money(detail.total)} />
                <MetaKV label="Balance" value={money(detail.balance)} />
                <MetaKV label="Company" value={detail.company_code || detail.company_id || "—"} />
                <MetaKV label="Last synced" value={short(detail.last_synced_at)} />
              </div>

              <div>
                <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Corrections ({(detail.corrections || []).length})
                </h4>
                <ul className="space-y-2">
                  {(detail.corrections || []).map((c) => (
                    <li
                      key={c.id}
                      className="rounded-md border border-slate-200 bg-white p-3"
                      data-testid={`invoice-preview-correction-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 whitespace-pre-line text-sm text-slate-800">
                          {c.description || "—"}
                        </div>
                        <div className="flex flex-shrink-0 items-center gap-2">
                          <span className="text-sm font-semibold text-slate-900">{money(c.amount)}</span>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${CORRECTION_STATUS_CLS[c.status] || "bg-slate-100 text-slate-700"}`}>
                            {c.status || "—"}
                          </span>
                        </div>
                      </div>
                      {(c.charge_allocations && c.charge_allocations.length > 0) && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {bucketBadges(c).map((b) => (
                            <span
                              key={b.key}
                              className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${b.cls}`}
                            >
                              {b.label}
                            </span>
                          ))}
                        </div>
                      )}
                      {(c.global_component || c.global_system) && (
                        <div className="mt-1.5 text-[11px] text-slate-500">
                          {c.global_component}{c.global_component && c.global_system ? " · " : ""}{c.global_system}
                        </div>
                      )}
                    </li>
                  ))}
                  {(!detail.corrections || detail.corrections.length === 0) && (
                    <li className="rounded-md border border-dashed border-slate-200 p-3 text-xs text-slate-500">
                      No corrections on this invoice.
                    </li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
}

function MetaKV({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`mt-0.5 text-sm text-slate-800 ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
    </div>
  );
}
